import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

import * as day from 'dayjs';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

import { UserpilotService } from '@app/src/app/shared/services/userpilot';

import { MediaService } from '@shared/services/media';

import { FormErrorMessages } from '../../constants';
import { FormAppearanceVariant, FormLabelBackground, FormSizes } from '../../types';
import { FormDateHeaderComponent } from '../form-date-range/components/form-date-header/form-date-header.component';

const DEFAULT_TIME = '12:00';
/**
 * The value is always a 24-hour HH:mm or HH:mm:ss
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input/time
 */
const DEFAULT_TIME_FORMAT = 'HH:mm';

@Component({
  selector: 'mg-form-date',
  templateUrl: './form-date.component.html',
  styleUrls: ['./form-date.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormDateComponent implements OnInit, OnDestroy, OnChanges {
  // Children
  @ViewChild('inputContainer') inputContainer;
  @ViewChild('picker') picker;

  // Constants
  public readonly FORM_ERROR_MESSAGES = FormErrorMessages;

  // Cleanup
  private _destroyedSubject = new ReplaySubject<void>(1);

  // State
  public formControl = new FormControl<day.Dayjs>(null);
  public timeControl = new FormControl<string>(null);
  private readonly _inputIsFocused$ = new BehaviorSubject<boolean>(false);
  public readonly inputIsFocused$ = this._inputIsFocused$.asObservable();
  public customHeader = FormDateHeaderComponent;

  // Inputs
  @Input() label = 'Date';
  @Input() labelBackground: FormLabelBackground = 'white';
  @Input() isClearable = false;
  @Input() widthSize: FormSizes = 'large';
  @Input() format = 'MMM D, YYYY';
  @Input() appearance?: FormAppearanceVariant = 'primary';
  @Input() hint: string;
  @Input() set control(formControl: FormControl | AbstractControl) {
    this.formControl = formControl as FormControl;
  }
  /** Set mininum date user select */
  @Input() minDate: day.Dayjs;
  /** Set maximum date user can select */
  @Input() maxDate: day.Dayjs;
  /**
   * Unique id for things like analytics and testing to hook into
   * Important to note changing this could break either of those
   */
  @Input() id: string;
  @Input() makeEndOfDay = false;

  /**
   * Alternative approach to using form control for value changes
   */
  @Input() value: day.Dayjs | null = null;
  @Output() dateChanged = new EventEmitter<day.Dayjs>();

  /**
   * Expose a time control along side date picker
   */
  @Input() showTime = false;

  public formattedDate$: Observable<string>;

  get isValid() {
    return this.formControl.valid && this.formControl.dirty && this.formControl.touched;
  }

  get inputDisabled() {
    return this.formControl.disabled;
  }

  get formStyleClasses(): Record<string, boolean> {
    return {
      [this.widthSize]: true,
      [this.labelBackground]: true,
    };
  }

  /** Component Constructor */
  constructor(public mediaService: MediaService, private _userpilot: UserpilotService) {}

  ngOnInit(): void {
    this.formattedDate$ = this.formControl.valueChanges.pipe(
      startWith(this.formControl.value),
      takeUntil(this._destroyedSubject),
      map((value: day.Dayjs) => value?.format(this.format) ?? ''),
    );

    this.timeControl.valueChanges.pipe(takeUntil(this._destroyedSubject)).subscribe((value: string) => {
      const date = this.formControl.value;
      if (!date) return;

      this._handleChange(date, value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value && !this.control) {
      this.formControl.setValue(this.value, { emitEvent: false });

      const time = this.value?.format(DEFAULT_TIME_FORMAT) ?? null;
      this.timeControl.setValue(time, { emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this._destroyedSubject.next();
    this._destroyedSubject.complete();
    this._inputIsFocused$.complete();
  }

  public openCalendar(): void {
    this._userpilot.track(this.id);
    this.picker?.open();
  }

  public async inputGainedFocus(): Promise<void> {
    this._inputIsFocused$.next(true);
  }

  public async inputLostFocus(): Promise<void> {
    this._inputIsFocused$.next(false);
  }

  public dateChange(event: MatDatepickerInputEvent<Date>) {
    let formattedDate = event.value ? day(event.value) : null;
    if (this.makeEndOfDay && formattedDate) {
      formattedDate = formattedDate.endOf('day');
    }

    const timeStr = this.timeControl.value || DEFAULT_TIME;
    this.timeControl.setValue(timeStr, { emitEvent: false });
    this.timeControl.markAsDirty();

    this._handleChange(formattedDate, timeStr);
  }

  public clearValue() {
    if (!this.isClearable) return;
    this.formControl.setValue(null, { emitEvent: true });
    this.formControl.markAsDirty();
    this._closePicker();
  }

  private _closePicker() {
    this.picker?.close();
  }

  private _handleChange(date: day.Dayjs, timeStr: string) {
    let newDate = date;

    if (this.showTime) {
      const formattedTime = this._timeStringToFormatted(timeStr);
      this.timeControl.setValue(formattedTime.format(DEFAULT_TIME_FORMAT), { emitEvent: false });
      newDate = date.hour(formattedTime.hour()).minute(formattedTime.minute());
    }

    this.formControl.setValue(newDate, { emitEvent: true });
    this.formControl.markAsDirty();
    this.dateChanged.emit(newDate);
  }

  private _timeStringToFormatted(timeStr: string): day.Dayjs {
    const time = day(timeStr, DEFAULT_TIME_FORMAT);
    return time.isValid() ? time : day(DEFAULT_TIME, DEFAULT_TIME_FORMAT);
  }
}
